import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Alert, IconButton, CircularProgress, Chip, TextField, Button,
  MenuItem, Divider, Tooltip, ToggleButton, ToggleButtonGroup, Autocomplete,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Home, Groups, Add, Delete, Person } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SubjectTeacherList() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [mode, setMode] = useState('teacher');   // 'teacher' | 'stage'

  const [years,    setYears]    = useState([]);
  const [year,     setYear]     = useState('');
  const [stages,   setStages]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [stageSubjects, setStageSubjects] = useState([]);   // subjects for the chosen stage/year
  const [rows,     setRows]     = useState([]);

  // Shared selections
  const [stageCode,  setStageCode]  = useState('');
  const [employeeNo, setEmployeeNo] = useState('');
  const [areaCodes,  setAreaCodes]  = useState([]);   // teacher-mode: multi-select subjects
  const [areaCode,   setAreaCode]   = useState('');   // stage-mode: single subject

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  // Reference data (once)
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/academics/years`, { headers: hdrs() }),
      axios.get(`${API}/api/stages`, { headers: hdrs() }),
      axios.get(`${API}/api/employees`, { headers: hdrs(), params: { department: 'Teaching' } }),
    ]).then(([y, s, t]) => {
      setYears(y.data || []);
      const active = (y.data || []).find(yr => yr.isActive) || (y.data || [])[0];
      if (active) setYear(active.year);
      setStages(s.data || []);
      setTeachers(t.data || []);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load reference data'))
      .finally(() => setLoading(false));
  }, []);

  const loadRows = useCallback(async () => {
    if (!year) return;
    try {
      const { data } = await axios.get(`${API}/api/academics/stage-subject-teachers`, { headers: hdrs(), params: { academicYear: year } });
      setRows(data || []);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load assignments'); }
  }, [year]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Subjects available for the chosen stage come from Stage Subjects.
  useEffect(() => {
    if (!year || !stageCode) { setStageSubjects([]); return; }
    axios.get(`${API}/api/academics/stage-learning-areas`, { headers: hdrs(), params: { year, stageCode } })
      .then(r => setStageSubjects(r.data || []))
      .catch(() => setStageSubjects([]));
  }, [year, stageCode]);

  const stageName = (code) => stages.find(s => s.code === code)?.name || code;

  const switchMode = (_, m) => {
    if (!m) return;
    setMode(m);
    setStageCode(''); setEmployeeNo(''); setAreaCode(''); setAreaCodes([]);
    setError('');
  };

  // Assign one (year, stage, subject, teacher) — shared by both modes.
  const postOne = (sc, ac, emp) =>
    axios.post(`${API}/api/academics/stage-subject-teachers`,
      { academicYear: year, stageCode: sc, areaCode: ac, employeeNo: emp }, { headers: hdrs() });

  const assign = async () => {
    setError('');
    if (mode === 'teacher') {
      if (!employeeNo || !stageCode || areaCodes.length === 0) {
        setError('Pick a teacher, a stage and at least one subject'); return;
      }
      setSaving(true);
      const results = await Promise.allSettled(areaCodes.map(ac => postOne(stageCode, ac, employeeNo)));
      setSaving(false);
      const added   = results.filter(r => r.status === 'fulfilled').length;
      const skipped = results.length - added;
      setAreaCodes([]);
      flash(`Assigned ${added} subject${added !== 1 ? 's' : ''}${skipped ? ` · ${skipped} already existed` : ''}`);
      loadRows();
    } else {
      if (!stageCode || !areaCode || !employeeNo) {
        setError('Pick a stage, a subject and a teacher'); return;
      }
      setSaving(true);
      try {
        await postOne(stageCode, areaCode, employeeNo);
        setAreaCode(''); setEmployeeNo('');
        flash('Teacher assigned');
        loadRows();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to assign');
      } finally { setSaving(false); }
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/academics/stage-subject-teachers/${id}`, { headers: hdrs() });
      flash('Assignment removed');
      loadRows();
    } catch (err) { setError(err.response?.data?.error || 'Failed to remove'); }
  };

  if (loading) return (
    <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /><Typography mt={2}>Loading…</Typography></Box>
  );

  const teacherField = (
    <TextField select size="small" label="Teacher" value={employeeNo}
      onChange={e => setEmployeeNo(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 220 } }}>
      <MenuItem value=""><em>Select teacher</em></MenuItem>
      {teachers.map(t => (
        <MenuItem key={t.employeeNo} value={t.employeeNo}>{t.firstName} {t.lastName} ({t.employeeNo})</MenuItem>
      ))}
    </TextField>
  );
  const stageField = (extra = {}) => (
    <TextField select size="small" label="Stage" value={stageCode}
      onChange={e => { setStageCode(e.target.value); setAreaCode(''); setAreaCodes([]); }}
      sx={{ minWidth: { xs: '100%', sm: 180 } }} {...extra}>
      <MenuItem value=""><em>Select stage</em></MenuItem>
      {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
    </TextField>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Groups sx={{ color: '#00897b' }} />
              <Typography variant="h6" fontWeight={700}>Subject Teachers</Typography>
            </Box>
            <Box>
              <TextField select size="small" label="Academic Year" value={year}
                onChange={e => setYear(e.target.value)} sx={{ minWidth: 140, mr: 1 }}>
                {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
              <Tooltip title="Academics Home">
                <IconButton onClick={() => navigate('/academics')}><Home /></IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Assign a teacher to a stage subject (e.g. “Grade 7 Mathematics”). The teacher then sees every class in
            that stage taking the subject, its students, and can enter marks and pull reports.
          </Typography>

          {/* Mode toggle */}
          <ToggleButtonGroup exclusive size="small" value={mode} onChange={switchMode} sx={{ mb: 2 }}>
            <ToggleButton value="teacher">By Teacher</ToggleButton>
            <ToggleButton value="stage">By Stage</ToggleButton>
          </ToggleButtonGroup>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Assign form */}
          <Box display="flex" flexWrap="wrap" gap={1.5} mb={2} alignItems="flex-start">
            {mode === 'teacher' ? (
              <>
                {teacherField}
                {stageField()}
                <Autocomplete
                  multiple size="small" disableCloseOnSelect
                  options={stageSubjects}
                  getOptionLabel={(o) => o.areaName || o.areaCode}
                  isOptionEqualToValue={(o, v) => o.areaCode === v.areaCode}
                  value={stageSubjects.filter(s => areaCodes.includes(s.areaCode))}
                  onChange={(_, val) => setAreaCodes(val.map(v => v.areaCode))}
                  disabled={!stageCode}
                  sx={{ minWidth: { xs: '100%', sm: 300 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Subjects"
                      placeholder={areaCodes.length ? '' : 'Pick subjects'}
                      helperText={stageCode && stageSubjects.length === 0 ? 'No subjects set for this stage' : 'Pick all subjects this teacher takes in the stage'} />
                  )}
                />
              </>
            ) : (
              <>
                {stageField()}
                <TextField select size="small" label="Subject" value={areaCode}
                  onChange={e => setAreaCode(e.target.value)} disabled={!stageCode}
                  helperText={stageCode && stageSubjects.length === 0 ? 'No subjects set for this stage' : ''}
                  sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                  <MenuItem value=""><em>Select subject</em></MenuItem>
                  {stageSubjects.map(s => <MenuItem key={s.areaCode} value={s.areaCode}>{s.areaName}</MenuItem>)}
                </TextField>
                {teacherField}
              </>
            )}
            <Button variant="contained" startIcon={saving ? <CircularProgress size={14} /> : <Add />}
              onClick={assign} disabled={saving} fullWidth={isMobile} sx={{ mt: { sm: 0.2 } }}>
              {mode === 'teacher' ? 'Assign Subjects' : 'Assign'}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Assignments list */}
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
              No subject teachers assigned for {year}.
            </Typography>
          ) : isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.2}>
              {rows.map(r => (
                <Card key={r._id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={700}>{r.employeeName || r.employeeNo}</Typography>
                      <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => remove(r._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {stageName(r.stageCode)} · {r.areaName || r.areaCode}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Stage</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Teacher</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r._id} hover>
                      <TableCell>{stageName(r.stageCode)}</TableCell>
                      <TableCell><Chip label={r.areaName || r.areaCode} size="small" variant="outlined" /></TableCell>
                      <TableCell>{r.employeeName || r.employeeNo}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => remove(r._id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
